import config from '@/utils/ApiUtils'
import { fetchWithToken } from '../utils/Keycloak'

export default {
  /**
   * Récupère tous les clients depuis Notion
   * @param {boolean} forceRefresh - Force le rafraîchissement du cache
   */
  getClients(forceRefresh = false) {
    const url = config().notionBackend + `client${forceRefresh ? '?force=true' : ''}`
    return fetchWithToken(url, {
      method: 'GET'
    }).then(response => {
      return response.json()
    })
  },

  /**
   * Récupère un client spécifique depuis Notion
   * @param {string} clientId - L'ID du client (idNotion)
   */
  getClient(clientId) {
    const url = config().notionBackend + `client/${clientId}`
    return fetchWithToken(url, {
      method: 'GET'
    }).then(response => {
      return response.json()
    })
  },

  /**
   * Récupère une page Notion (magasin) par son ID
   * @param {string} pageId - L'ID de la page Notion
   */
  getMagasin(pageId) {
    const url = config().notionBackend + `page/${pageId}`
    return fetchWithToken(url, {
      method: 'GET'
    }).then(response => {
      return response.json()
    })
  },

  /**
   * Récupère les contacts d'un magasin spécifique depuis Notion
   * @param {string} magasinPageId - L'ID de la page Notion du magasin
   * @param {boolean} forceRefresh - Force le rafraîchissement du cache
   */
  getContacts(magasinPageId, forceRefresh = false) {
    const url = config().notionBackend + `contact?magasinPageId=${magasinPageId}${forceRefresh ? '&force=true' : ''}`
    return fetchWithToken(url, {
      method: 'GET'
    }).then(response => {
      return response.json()
    })
  },

  /**
   * Derniers tickets de la base Développement liés à au moins un magasin (carte de l'accueil)
   * @param {number} limit - Nombre de tickets (max 50)
   * @param {boolean} forceRefresh - Force le rafraîchissement du cache
   */
  getRecentTickets(limit = 8, forceRefresh = false) {
    const params = new URLSearchParams({ limit: String(limit) })
    if (forceRefresh) params.append('force', 'true')
    return fetchWithToken(`${config().notionBackend}ticket/recent?${params.toString()}`, {
      method: 'GET'
    }).then(response => {
      if (!response.ok) throw new Error(`Erreur ${response.status} lors de la récupération des tickets`)
      return response.json()
    })
  },

  /**
   * Récupère la dernière version de l'application Poissonnerie
   */
  getPoissonnerieVersion() {
    const url = config().notionBackend + 'version';
    return fetchWithToken(url, { method: 'GET' }).then(response => {
      if (!response.ok) throw new Error('Erreur lors de la récupération de la version');
      return response.json();
    }).then(data => data.version);
  },

  /**
   * Crée un nouveau magasin dans Notion
   * @param {Object} magasinData - Les données du magasin à créer
   */
  createMagasin(magasinData) {
    const url = config().notionBackend + 'magasin';
    return fetchWithToken(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(magasinData)
    }).then(response => {
      if (!response.ok) {
        throw new Error('Erreur lors de la création du magasin dans Notion');
      }
      return response.json();
    });
  },

  /**
   * Récupère les commentaires d'un magasin
   * @param {string} magasinPageId - ID de la page Notion du magasin
   * @param {string} [context] - Filtre optionnel : 'poissonnerie' | 'boucherie'
   */
  getComments(magasinPageId, context = null) {
    const params = new URLSearchParams({ magasinPageId })
    if (context) params.append('context', context)
    return fetchWithToken(`${config().notionBackend}comments?${params.toString()}`, {
      method: 'GET'
    }).then(response => {
      if (!response.ok) throw new Error(`Erreur ${response.status} lors de la récupération des commentaires`)
      return response.json()
    })
  },

  /**
   * Crée un commentaire sur un magasin
   * @param {Object} payload
   * @param {string} payload.magasinPageId
   * @param {string} payload.context - 'poissonnerie' | 'boucherie'
   * @param {string} payload.content
   * @param {string} [payload.author]
   * @param {string} [payload.date] - YYYY-MM-DD (défaut : aujourd'hui)
   */
  createComment({ magasinPageId, context, content, author = null, date = null }) {
    const body = { magasinPageId, context, content }
    if (author) body.author = author
    if (date) body.date = date
    return fetchWithToken(`${config().notionBackend}comments`, {
      method: 'POST',
      body: JSON.stringify(body)
    }).then(response => {
      if (!response.ok) throw new Error(`Erreur ${response.status} lors de la création du commentaire`)
      return response.json()
    })
  },

  /**
   * Supprime un commentaire
   * @param {string} commentId
   */
  deleteComment(commentId) {
    return fetchWithToken(`${config().notionBackend}comments/${commentId}`, {
      method: 'DELETE'
    }).then(response => {
      if (response.status !== 204 && !response.ok) {
        throw new Error(`Erreur ${response.status} lors de la suppression du commentaire`)
      }
    })
  },
}
